import productCarbonAssessment from '../assets/pre-login/product-carbon-assessment.svg';
import productDashboardWeb from '../assets/pre-login/product-dashboard-web.svg';
import productDocumentManagement from '../assets/pre-login/product-document-management.svg';
import productGreenLoans from '../assets/pre-login/product-green-loans.svg';
import productReporting from '../assets/pre-login/product-reporting.svg';
import platformLoop from '../assets/pre-login/platform-loop.svg';
import type { ReadinessLocale } from '../i18n/readinessLocale';
import { GLOSSARY_ILLUSTRATION_ALT_LOCALE } from '../i18n/glossaryLocale';

const MSME_SCOPE_SVG = {
  scope_direct: '/assets/msme/scope-1-direct.svg',
  scope_electricity: '/assets/msme/scope-2-electricity.svg',
  scope_supply_chain: '/assets/msme/scope-3-supply-chain.svg',
} as const;

/** Illustration keys for glossary / plain-language explainers. */
export type GlossaryIllustrationKey =
  | 'documents'
  | 'carbon_assessment'
  | 'reporting'
  | 'dashboard'
  | 'finance'
  | 'workflow'
  | 'scope_direct'
  | 'scope_electricity'
  | 'scope_supply_chain';

export const GLOSSARY_ILLUSTRATIONS: Record<GlossaryIllustrationKey, { src: string; alt: string }> = {
  documents: {
    src: productDocumentManagement,
    alt: 'Illustration of uploading bills and invoices for automatic reading.',
  },
  carbon_assessment: {
    src: productCarbonAssessment,
    alt: 'Illustration of a carbon footprint assessment with category breakdown.',
  },
  reporting: {
    src: productReporting,
    alt: 'Illustration of compliance and sustainability reports for buyers and banks.',
  },
  dashboard: {
    src: productDashboardWeb,
    alt: 'Illustration of a dashboard showing carbon score and emissions trends.',
  },
  finance: {
    src: productGreenLoans,
    alt: 'Illustration linking carbon performance to green loans and schemes.',
  },
  workflow: {
    src: platformLoop,
    alt: 'Illustration of the measure–act–report workflow loop.',
  },
  scope_direct: {
    src: MSME_SCOPE_SVG.scope_direct,
    alt: 'Direct on-site emissions such as fuel burned in your factory or vehicles.',
  },
  scope_electricity: {
    src: MSME_SCOPE_SVG.scope_electricity,
    alt: 'Emissions from electricity purchased from the grid for your operations.',
  },
  scope_supply_chain: {
    src: MSME_SCOPE_SVG.scope_supply_chain,
    alt: 'Indirect emissions from materials, transport, and suppliers in your value chain.',
  },
};

/** Map glossary term ids to illustration keys. */
export const GLOSSARY_TERM_ILLUSTRATIONS: Record<string, GlossaryIllustrationKey> = {
  ocr: 'documents',
  carbon_footprint: 'carbon_assessment',
  carbon_score: 'dashboard',
  scope1: 'scope_direct',
  scope_1: 'scope_direct',
  scope2: 'scope_electricity',
  scope_2: 'scope_electricity',
  scope3: 'scope_supply_chain',
  scope_3: 'scope_supply_chain',
  ghg: 'carbon_assessment',
  tco2e: 'carbon_assessment',
  emission_factor: 'carbon_assessment',
  brsr: 'reporting',
  iso14064: 'reporting',
  cbam: 'reporting',
  csrd: 'reporting',
  cdp: 'reporting',
  ecovadis: 'reporting',
  adeetie: 'finance',
  gift: 'finance',
  msme: 'finance',
};

export const getGlossaryIllustration = (termId: string, locale: ReadinessLocale = 'en') => {
  const key = GLOSSARY_TERM_ILLUSTRATIONS[termId];
  if (!key) {
    return null;
  }
  const base = GLOSSARY_ILLUSTRATIONS[key];
  const localizedAlt = GLOSSARY_ILLUSTRATION_ALT_LOCALE[locale]?.[key];
  return {
    src: base.src,
    alt: localizedAlt ?? base.alt,
  };
};
